import type { Erwartungszeile } from "../daten/routen";
import type { Position } from "../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Was „Kunde holt ab" an der Positionsliste ändert — ohne Oberfläche,
   damit es sich prüfen lässt (Paket 9, Angebot Runde 4, 08.10.2026).

   **1 · Die Anlieferung bleibt an ihrem Platz.** Luca: *„Wenn ich bei
   ‚Anlieferung‘ ‚Kunde holt ab‘ anhake, rutscht die Position auf Position 2.
   Warum auch immer. Sie soll da bleiben, wo sie ist."*

   Die Wurzel: Die Anlieferung ohne Preis ist keine gespeicherte Position,
   sondern eine Zeile aus der Regel (`Erwartungszeile`). Der Haken legt an
   ihrer Stelle eine **gespeicherte** 0,00-Zeile an (`dienste/abholung.ts`,
   `abholungSchalten`), und die Liste stellte jede gespeicherte Zeile vor
   alle Regelzeilen und zählte die Regelzeilen erst danach
   (`Positionsliste`: `[...geordnet.folge, ...regelzeilen]`, Nummer
   `letzteNummer + p.nr + 1`). Bei einer Miete wurde aus „4 Anlieferung"
   also „2 Selbstabholung", und Versicherung und Check rückten nach.

   Jetzt steht die abgewählte Anlieferung in der Folge der Regelzeilen, an
   dem Platz ihrer Regel (`zuschlagReihenfolge` = `surcharge_rule.sort_order`
   = `Erwartungszeile.reihenfolge`). Kein Betrag ändert sich, nur der Platz.

   **2 · Holt der Kunde ab, fährt keiner von uns mit.** Luca: *„Wenn ‚Kunde
   holt ab‘ angehakt ist, brauche ich nicht die Möglichkeit, dass er sagt
   ‚Ich gebe den Bediener mit‘. Dann wäre es ja eine ganz normale
   Auslieferung."* Die Bedienungspflicht kommt aus dem Stamm (`packages/fach`,
   `mitBediener`, `bedienerPflicht`):

   | Pflicht          | geliefert          | abgeholt                               |
   |------------------|--------------------|----------------------------------------|
   | `ohne_person`    | nichts             | nichts                                 |
   | `wahlweise`      | Haken „mit Bediener" | **kein Haken** (der Schalter hebt ihn auf) |
   | `nur_mit_person` | „nur mit Bediener" | **Widerspruch** — offen, nicht verboten |
   ══════════════════════════════════════════════════════════════════ */

/** Steht diese gespeicherte Zeile an der Stelle einer Regelzeile? Die abgewählte Anlieferung. */
export function anRegelstelle(p: Position): boolean {
  return p.abholzeile === true && typeof p.zuschlagReihenfolge === "number"
    && !p.alternativeZu && !p.optional;
}

/** Eine Zeile hinter den Positionen: aus einer Regel, oder gespeichert an deren Platz. */
export type Hintenzeile = { regel: Erwartungszeile; zeile?: undefined } | { zeile: Position; regel?: undefined };

/**
 * Die Folge der Liste: `vorne` die Positionen (ohne die Zeilen an einer
 * Regelstelle), `hinten` die Regelzeilen in der Reihenfolge des Stamms, mit
 * den gespeicherten Zeilen an ihrem Regelplatz dazwischen.
 */
export function listenfolge(
  zeilen: readonly Position[],
  regeln: readonly Erwartungszeile[] | undefined,
): { vorne: Position[]; hinten: Hintenzeile[] } {
  const vorne = zeilen.filter((p) => !anRegelstelle(p));
  const alle = [
    ...(regeln ?? []).map((z, i) => ({ h: { regel: z } as Hintenzeile, platz: z.reihenfolge, i })),
    ...zeilen.filter(anRegelstelle).map((p, i) => ({
      h: { zeile: p } as Hintenzeile, platz: p.zuschlagReihenfolge as number, i: (regeln?.length ?? 0) + i,
    })),
  ];
  alle.sort((a, b) => a.platz - b.platz || a.i - b.i);
  return { vorne, hinten: alle.map((x) => x.h) };
}

/** Was „Kunde holt ab" für den Bediener einer Miete heißt. */
export type Bedienerlage = "keiner" | "wahl" | "entfaellt" | "widerspruch";

export function bedienerBeiAbholung(p: Pick<Position, "art" | "bedienungspflicht" | "abgeholt" | "anbauAn">): Bedienerlage {
  if (p.art !== "miete" || p.anbauAn || !p.bedienungspflicht || p.bedienungspflicht === "ohne_person") return "keiner";
  if (!p.abgeholt) return "wahl";
  return p.bedienungspflicht === "nur_mit_person" ? "widerspruch" : "entfaellt";
}

/** Die Mieten, die nur mit Bediener gehen und trotzdem abgeholt werden — ein offener Punkt. */
export function abholungGegenPflicht(zeilen: readonly Position[]): Position[] {
  return zeilen.filter((p) => bedienerBeiAbholung(p) === "widerspruch");
}
